import React, { useState } from "react";
import {
  IconBrandInstagram,
  IconBrandPinterest,
  IconBrandFacebook,
  IconBrandTwitter,
  IconBrandYoutube,
  IconBrandLinkedin,
  IconBrandTelegram,
} from "@tabler/icons-react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import Logo from "./Logo";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.8 },
    });
    setIsSubscribed(true);
  };

  const socialLinks = [
    {
      icon: IconBrandInstagram,
      href: "https://instagram.com/wellnessbymercy",
      label: "Instagram",
    },
    {
      icon: IconBrandPinterest,
      href: "https://www.pinterest.com/wellnessbymercy/",
      label: "Pinterest",
    },
    {
      icon: IconBrandFacebook,
      href: "https://facebook.com/wellnessbymercy",
      label: "Facebook",
    },
    {
      icon: IconBrandTwitter,
      href: "https://twitter.com/wellnessbymercy",
      label: "Twitter",
    },
    {
      icon: IconBrandYoutube,
      href: "https://www.youtube.com/c/wellnessbymercy",
      label: "YouTube",
    },
    {
      icon: IconBrandLinkedin,
      href: "https://linkedin.com/in/wellnessbymercy",
      label: "LinkedIn",
    },
    { icon: IconBrandTelegram, href: "#", label: "Telegram" },
  ];

  return (
    <footer className="bg-stone-950 text-stone-400 pt-24 pb-12 border-t border-white/5">
      <div className="container mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-20">
          {/* Brand */}
          <div className="md:col-span-5">
            <a
              href="#home"
              className="mb-8 block text-stone-100 hover:opacity-90 transition-opacity"
            >
              <Logo />
            </a>
            <p className="text-lg font-light leading-relaxed max-w-sm mb-4">
              Nurturing whole person wellness through evidence-based nutrition
              and mindful movement.
            </p>
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-stone-500 mb-8">
              Healing • Health • Happiness
            </p>

            <div className="mb-4 text-sm font-semibold uppercase tracking-wider text-stone-300">
              Get in Touch
            </div>
            <div className="flex flex-wrap gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="text-stone-400 hover:text-white transition-all duration-300 p-3 hover:bg-white/10 rounded-full hover:scale-110"
                >
                  <social.icon size={28} stroke={1.5} />
                </a>
              ))}
            </div>
          </div>

          {/* Links Column 1 */}
          <div className="md:col-span-2 md:col-start-7">
            <h4 className="text-sm font-bold uppercase tracking-widest text-stone-100 mb-8">
              Explore
            </h4>
            <ul className="space-y-4">
              <li>
                <a
                  href="#services"
                  className="hover:text-white transition-colors font-light"
                >
                  Services
                </a>
              </li>
              <li>
                <a
                  href="#journey"
                  className="hover:text-white transition-colors font-light"
                >
                  Journey
                </a>
              </li>
              <li>
                <a
                  href="#gallery"
                  className="hover:text-white transition-colors font-light"
                >
                  Gallery
                </a>
              </li>
              <li>
                <a
                  href="#about"
                  className="hover:text-white transition-colors font-light"
                >
                  About Me
                </a>
              </li>
            </ul>
          </div>

          {/* Links Column 2 */}
          <div className="md:col-span-2">
            <h4 className="text-sm font-bold uppercase tracking-widest text-stone-100 mb-8">
              Contact
            </h4>
            <ul className="space-y-4">
              <li>
                <a
                  href="mailto:wellnessbymercy@gmail.com"
                  className="hover:text-white transition-colors font-light"
                >
                  Email Us
                </a>
              </li>
              <li>
                <a
                  href="tel:+918850227415"
                  className="hover:text-white transition-colors font-light"
                >
                  Call Us
                </a>
              </li>
              <li className="font-light text-stone-600">Mumbai, India</li>
            </ul>
          </div>

          {/* Newsletter */}
          <div className="md:col-span-3">
            <h4 className="text-sm font-bold uppercase tracking-widest text-stone-100 mb-8">
              Newsletter
            </h4>
            <AnimatePresence mode="wait">
              {isSubscribed ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-white/5 border border-white/10 rounded-xl p-6 text-center"
                >
                  <p className="text-stone-100 font-serif text-lg italic">
                    Awesome, your health journey begins now!
                  </p>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="flex flex-col space-y-4"
                  onSubmit={handleSubscribe}
                >
                  <input
                    type="email"
                    required
                    placeholder="Email address"
                    className="bg-white/5 border border-white/10 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-white/30 transition-all placeholder:text-stone-600"
                  />
                  <button
                    type="submit"
                    className="bg-stone-100 text-stone-950 font-medium px-4 py-3 rounded-lg hover:bg-white transition-colors"
                  >
                    Subscribe
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center">
          <p className="text-stone-600 text-sm mb-4 md:mb-0 font-light">
            © {currentYear} Wellness by Mercy. All rights reserved.
          </p>
          <div className="flex space-x-8 text-sm text-stone-600 font-light">
            <a href="#" className="hover:text-white transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-white transition-colors">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
